<template>
  <div class="robot-page">
    <DigitalHuman
      :state="state"
      :text="displayText"
      :avatarSrc="avatarSrc"
    />

    <!-- Error display -->
    <div v-if="error" class="error-toast">{{ error }}</div>

    <!-- Start/stop button -->
    <button class="mic-btn" @click="toggleMic" :class="{ active: micActive }">
      {{ micActive ? '⏹ 停止监听' : '🎤 开始对话' }}
    </button>

    <!-- Conversation log (collapsible) -->
    <details class="convo-log">
      <summary>对话记录 ({{ messages.length }})</summary>
      <div class="log-list">
        <div
          v-for="(msg, i) in messages"
          :key="i"
          :class="['log-msg', msg.role]"
        >
          <strong>{{ msg.role === 'user' ? '你' : '机器人' }}:</strong>
          {{ msg.content }}
        </div>
      </div>
    </details>
  </div>
</template>

<script>
import axios from 'axios'
import DigitalHuman from './DigitalHuman.vue'
import { AudioCapture } from './audio.js'
import { API_URL } from '@/constants/const.js'

export default {
  name: 'RobotChat',
  components: { DigitalHuman },
  data() {
    return {
      state: 'idle',       // idle | listening | thinking | speaking
      micActive: false,
      error: null,
      messages: [],         // [{role, content}]
      avatarSrc: '',        // can be set to custom avatar URL
      audioCapture: null,
    }
  },
  computed: {
    displayText() {
      if (this.state === 'listening') return '正在倾听...'
      if (this.state === 'thinking') return '思考中...'
      if (this.state === 'speaking') {
        const last = this.messages[this.messages.length - 1]
        return last && last.role === 'assistant' ? last.content : ''
      }
      return ''
    },
  },
  methods: {
    async toggleMic() {
      if (this.micActive) {
        this.stopMic()
      } else {
        await this.startMic()
      }
    },

    async startMic() {
      try {
        this.audioCapture = new AudioCapture()
        this.audioCapture.onSpeechStart = () => {
          this.state = 'listening'
          this.error = null
          // Interrupt current playback if user starts speaking
          this.audioCapture.stopPlayback()
        }
        this.audioCapture.onSpeechEnd = async (wavBlob) => {
          await this.handleSpeech(wavBlob)
        }
        this.audioCapture.onPlayEnd = () => {
          this.state = 'idle'
        }
        await this.audioCapture.start()
        this.micActive = true
        this.state = 'idle'
      } catch (err) {
        console.error('Mic access denied:', err)
        this.error = '无法访问麦克风，请检查浏览器权限'
      }
    },

    stopMic() {
      if (this.audioCapture) {
        this.audioCapture.stop()
        this.audioCapture = null
      }
      this.micActive = false
      this.state = 'idle'
    },

    async handleSpeech(wavBlob) {
      this.state = 'thinking'
      this.error = null

      try {
        const formData = new FormData()
        formData.append('audio', wavBlob, 'speech.wav')

        // Send recent conversation history (last 10 rounds = 20 messages)
        const recentHistory = this.messages.slice(-20)
        formData.append('history', JSON.stringify(recentHistory))
        let url = API_URL["robotChat"];
        const res = await axios.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
        })

        const data = res.data

        if (data.error) {
          // Handle specific error codes
          if (data.error === 'stt_empty') {
            this.error = data.msg || '没听清，请再说一次'
          } else {
            this.error = data.msg || '服务异常，请重试'
          }
          this.state = 'idle'
          return
        }

        // Update conversation history
        if (data.user_text) {
          this.messages.push({ role: 'user', content: data.user_text })
        }
        if (data.reply_text) {
          this.messages.push({ role: 'assistant', content: data.reply_text })
        }

        // Play TTS audio
        if (data.reply_audio) {
          this.state = 'speaking'
          await this.audioCapture.playAudio(data.reply_audio)
          this.state = 'idle'
        } else {
          // Text-only response (TTS degraded)
          this.state = 'idle'
        }
      } catch (err) {
        console.error('Chat error:', err)
        this.error = '通信失败，请检查网络连接'
        this.state = 'idle'
      }
    },
  },
  beforeDestroy() {
    this.stopMic()
  },
}
</script>

<style scoped>
.robot-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(180deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  padding: 20px;
}

.error-toast {
  background: rgba(239, 68, 68, 0.9);
  color: #fff;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 12px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.mic-btn {
  margin-top: 24px;
  padding: 12px 36px;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  backdrop-filter: blur(10px);
  transition: all 0.2s;
}
.mic-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}
.mic-btn.active {
  background: #ef4444;
}

.convo-log {
  margin-top: 30px;
  width: 100%;
  max-width: 500px;
  color: #ccc;
  font-size: 13px;
}
.convo-log summary {
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
}
.log-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  margin-top: 6px;
}
.log-msg {
  padding: 4px 0;
  line-height: 1.5;
}
.log-msg.user { color: #93c5fd; }
.log-msg.assistant { color: #86efac; }

/* Mobile responsive */
@media (max-width: 768px) {
  .mic-btn {
    padding: 16px 48px;
    font-size: 18px;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }
  .convo-log {
    max-width: 100%;
    font-size: 14px;
  }
  .error-toast {
    font-size: 15px;
    padding: 10px 24px;
  }
}
</style>
